package br.com.vvrefeicoes.portal.feature.guides

import br.com.vvrefeicoes.portal.core.util.PdfCanvasDocument
import br.com.vvrefeicoes.portal.core.util.PdfCanvasPage
import br.com.vvrefeicoes.portal.core.util.PdfColor
import br.com.vvrefeicoes.portal.core.util.PdfImage
import br.com.vvrefeicoes.portal.core.util.PdfLink
import br.com.vvrefeicoes.portal.core.util.PdfOp
import br.com.vvrefeicoes.portal.core.util.PdfPageSize
import br.com.vvrefeicoes.portal.core.util.PdfText
import br.com.vvrefeicoes.portal.domain.model.Guide
import br.com.vvrefeicoes.portal.domain.model.GuideBlock
import br.com.vvrefeicoes.portal.domain.model.GuideBlockType
import br.com.vvrefeicoes.portal.feature.notifications.htmlToPlain
import kotlin.math.min

data class GuidePdfLabels(
    val version: String,
    val slogan: String,
    val rights: String,
    val availableAt: String,
    val videoMedia: String,
    val pdfMedia: String,
)

data class GuidePdfAssets(
    val logoPortal: PdfImage,
    val logoVv: PdfImage,
    val videoIcon: PdfImage,
    val pdfIcon: PdfImage,
)

private const val PAGE_MARGIN = 36f
private const val HEADER_TEXT_TOP = 28f
private const val COVER_HEADER_LOGO_TOP = 24f
private const val COVER_HEADER_SLOGAN_TOP = 46f
private const val CONTENT_TOP = 74f
private const val CONTENT_BOTTOM = 740f
private const val FOOTER_TOP = 756f
private const val BRAND_LOGO_HEIGHT = 18f
private const val COVER_FOOTER_RIGHTS_TOP = 778f
private const val FOOTER_LINK_TOP = 778f
private const val FOOTER_PAGE_TOP = 778f
private const val FOOTER_MIN_GAP = 16f
private const val TEXT_LINE_HEIGHT = 14f
private const val MEDIA_ICON_SIZE = 18f
private const val MAX_IMAGE_HEIGHT = 250f

private val TEXT_COLOR = PdfColor(33, 37, 41)
private val MUTED_COLOR = PdfColor(108, 117, 125)
private val LINK_COLOR = PdfColor(0, 95, 115)

fun buildGuidePdf(
    guide: Guide,
    labels: GuidePdfLabels,
    assets: GuidePdfAssets,
    shareLink: String,
    images: Map<String, PdfImage> = emptyMap(),
): PdfCanvasDocument {
    val pageSize = PdfPageSize.A4_PORTRAIT
    val pages = mutableListOf<MutablePage>()
    val contentWidth = pageSize.width - PAGE_MARGIN * 2f

    fun newPage(pageNumber: Int?): MutablePage {
        val page = MutablePage()
        if (pageNumber != null) {
            page.drawHeader(
                title = guide.title,
                version = labels.version,
                contentWidth = contentWidth,
            )
            page.drawFooter(
                pageSize = pageSize,
                labels = labels,
                shareLink = shareLink,
                pageNumber = pageNumber,
            )
        }
        pages += page
        return page
    }

    val cover = newPage(pageNumber = null)
    cover.drawCoverHeader(labels, assets.logoPortal)
    cover.drawCoverFooter(
        pageSize = pageSize,
        labels = labels,
        logo = assets.logoVv,
    )
    cover.drawCover(
        pageSize = pageSize,
        contentWidth = contentWidth,
        title = guide.title,
        summary = guide.description,
    )

    if (guide.blocks.isNotEmpty()) {
        var current = newPage(pageNumber = pages.size + 1)
        var y = CONTENT_TOP

        fun newContentPage() {
            current = newPage(pageNumber = pages.size + 1)
            y = CONTENT_TOP
        }

        fun ensureSpace(height: Float) {
            if (y + height > CONTENT_BOTTOM) {
                newContentPage()
            }
        }

        fun drawCenteredCaption(caption: String) {
            if (caption.isBlank()) return
            val lines = PdfText.wrap(caption, contentWidth, 8f, maxLines = 2)
            lines.forEach { line ->
                ensureSpace(11f)
                current.text(
                    x = (pageSize.width - PdfText.width(line, 8f)) / 2f,
                    top = y,
                    value = line,
                    size = 8f,
                    color = MUTED_COLOR,
                )
                y += 11f
            }
        }

        fun drawMediaBlock(
            block: GuideBlock,
            icon: PdfImage,
            message: String,
        ) {
            val textX = PAGE_MARGIN + MEDIA_ICON_SIZE + 10f
            val textWidth = contentWidth - MEDIA_ICON_SIZE - 10f
            val messageLines = PdfText.wrap(message, textWidth, 9f)
            val extra =
                listOfNotNull(
                    block.caption.takeIf { it.isNotBlank() },
                    block.fileName.takeIf { it.isNotBlank() },
                )
            val extraLines = extra.flatMap { PdfText.wrap(it, textWidth, 8f, maxLines = 2) }
            val blockHeight =
                maxOf(
                    MEDIA_ICON_SIZE,
                    messageLines.size * 12f + extraLines.size * 10f,
                )
            ensureSpace(blockHeight + 14f)
            current.image(
                image = icon,
                x = PAGE_MARGIN,
                top = y,
                width = MEDIA_ICON_SIZE,
                height = MEDIA_ICON_SIZE,
            )
            var textTop = y
            messageLines.forEach { line ->
                current.text(
                    x = textX,
                    top = textTop,
                    value = line,
                    size = 9f,
                    color = TEXT_COLOR,
                )
                textTop += 12f
            }
            extraLines.forEach { line ->
                current.text(
                    x = textX,
                    top = textTop,
                    value = line,
                    size = 8f,
                    color = MUTED_COLOR,
                )
                textTop += 10f
            }
            y += blockHeight + 14f
        }

        guide.blocks.forEach { block ->
            when (block.type) {
                GuideBlockType.TEXT -> {
                    val lines = PdfText.wrap(htmlToPlain(block.html), contentWidth, 10f)
                    lines.forEach { line ->
                        ensureSpace(TEXT_LINE_HEIGHT)
                        current.text(
                            x = PAGE_MARGIN,
                            top = y,
                            value = line,
                            size = 10f,
                            color = TEXT_COLOR,
                        )
                        y += TEXT_LINE_HEIGHT
                    }
                    y += 8f
                }
                GuideBlockType.IMAGE -> {
                    val image = images[block.documentId]
                    if (image == null) {
                        ensureSpace(24f)
                        current.text(
                            x = PAGE_MARGIN,
                            top = y,
                            value = "Imagem disponível apenas na versão online do manual, no Portal VV.",
                            size = 9f,
                            color = MUTED_COLOR,
                        )
                        y += 24f
                    } else {
                        val scale =
                            min(
                                contentWidth / image.width.toFloat(),
                                MAX_IMAGE_HEIGHT / image.height.toFloat(),
                            ).coerceAtMost(1f)
                        val imageWidth = image.width * scale
                        val imageHeight = image.height * scale
                        ensureSpace(imageHeight + 24f)
                        current.image(
                            image = image,
                            x = (pageSize.width - imageWidth) / 2f,
                            top = y,
                            width = imageWidth,
                            height = imageHeight,
                        )
                        y += imageHeight + 6f
                    }
                    drawCenteredCaption(block.caption)
                    y += 8f
                }
                GuideBlockType.VIDEO ->
                    drawMediaBlock(block, assets.videoIcon, labels.videoMedia)
                GuideBlockType.PDF ->
                    drawMediaBlock(block, assets.pdfIcon, labels.pdfMedia)
            }
        }
    }

    return PdfCanvasDocument(
        pageSize = pageSize,
        pages = pages.map { it.toPage() },
    )
}

private class MutablePage {
    private val ops = mutableListOf<PdfOp>()
    private val links = mutableListOf<PdfLink>()

    fun text(
        x: Float,
        top: Float,
        value: String,
        size: Float,
        color: PdfColor,
        bold: Boolean = false,
    ) {
        if (value.isBlank()) return
        ops += PdfOp.Text(x = x, y = PAGE_HEIGHT - top - size, text = value, size = size, bold = bold, color = color)
    }

    fun image(
        image: PdfImage,
        x: Float,
        top: Float,
        width: Float,
        height: Float,
    ) {
        ops +=
            PdfOp.Image(
                image = image,
                x = x,
                y = PAGE_HEIGHT - top - height,
                width = width,
                height = height,
            )
    }

    fun link(
        x: Float,
        top: Float,
        width: Float,
        height: Float,
        url: String,
    ) {
        links += PdfLink(x = x, top = top, width = width, height = height, url = url)
    }

    fun drawHeader(
        title: String,
        version: String,
        contentWidth: Float,
    ) {
        val versionWidth = if (version.isBlank()) 0f else PdfText.width(version, 8f, bold = true)
        val titleMaxWidth = contentWidth - versionWidth - if (version.isBlank()) 0f else FOOTER_MIN_GAP
        val titleText = PdfText.ellipsize(title, titleMaxWidth, 10f, bold = true)
        text(PAGE_MARGIN, HEADER_TEXT_TOP, titleText, 10f, TEXT_COLOR, bold = true)
        if (version.isNotBlank()) {
            textRight(version, HEADER_TEXT_TOP, 8f, TEXT_COLOR, bold = true)
        }
    }

    fun drawCoverHeader(
        labels: GuidePdfLabels,
        logo: PdfImage,
    ) {
        val logoWidth = BRAND_LOGO_HEIGHT * logo.width.toFloat() / logo.height
        image(logo, x = PAGE_MARGIN, top = COVER_HEADER_LOGO_TOP, width = logoWidth, height = BRAND_LOGO_HEIGHT)
        if (labels.slogan.isNotBlank()) {
            text(PAGE_MARGIN, COVER_HEADER_SLOGAN_TOP, labels.slogan, 6.5f, MUTED_COLOR)
        }
    }

    fun drawCoverFooter(
        pageSize: PdfPageSize,
        labels: GuidePdfLabels,
        logo: PdfImage,
    ) {
        val logoWidth = BRAND_LOGO_HEIGHT * logo.width.toFloat() / logo.height
        image(
            logo,
            x = pageSize.width - PAGE_MARGIN - logoWidth,
            top = FOOTER_TOP,
            width = logoWidth,
            height = BRAND_LOGO_HEIGHT,
        )
        textRight(labels.rights, COVER_FOOTER_RIGHTS_TOP, 7f, MUTED_COLOR)
    }

    fun drawCover(
        pageSize: PdfPageSize,
        contentWidth: Float,
        title: String,
        summary: String,
    ) {
        val titleLines = PdfText.wrap(title, contentWidth, 30f, bold = true, maxLines = 3)
        val titleHeight = titleLines.size * 38f
        val centerTop = (CONTENT_TOP + (FOOTER_TOP - 18f)) / 2f
        var titleTop = centerTop - titleHeight / 2f
        titleLines.forEach { line ->
            val x = (pageSize.width - PdfText.width(line, 30f, bold = true)) / 2f
            text(x, titleTop, line, 30f, TEXT_COLOR, bold = true)
            titleTop += 38f
        }
        if (summary.isNotBlank()) {
            val summaryWidth = contentWidth * 0.68f
            val lines = PdfText.wrap(summary, summaryWidth, 10f)
            var summaryTop = titleTop + 18f
            lines.forEach { line ->
                val x = pageSize.width - PAGE_MARGIN - PdfText.width(line, 10f)
                text(x, summaryTop, line, 10f, MUTED_COLOR)
                summaryTop += 14f
            }
        }
    }

    fun drawFooter(
        pageSize: PdfPageSize,
        labels: GuidePdfLabels,
        shareLink: String,
        pageNumber: Int?,
    ) {
        val pageText = pageNumber?.toString().orEmpty()
        val pageWidth = if (pageText.isBlank()) 0f else PdfText.width(pageText, 7f)
        val linkAreaLeft = PAGE_MARGIN
        val linkAreaRight = pageSize.width - PAGE_MARGIN - pageWidth - FOOTER_MIN_GAP
        val linkAreaWidth = (linkAreaRight - linkAreaLeft).coerceAtLeast(1f)
        val available = "${labels.availableAt} $shareLink".trim()
        val linkText = PdfText.ellipsize(available, linkAreaWidth, 6.5f)
        val linkWidth = PdfText.width(linkText, 6.5f)
        val linkX = linkAreaLeft + (linkAreaWidth - linkWidth) / 2f
        text(linkX, FOOTER_LINK_TOP, linkText, 6.5f, LINK_COLOR)
        link(linkX - 2f, FOOTER_LINK_TOP - 2f, linkWidth + 4f, 10f, shareLink)

        if (pageNumber != null) {
            textRight(pageText, FOOTER_PAGE_TOP, 7f, MUTED_COLOR)
        }
    }

    private fun textRight(
        value: String,
        top: Float,
        size: Float,
        color: PdfColor,
        bold: Boolean = false,
    ) {
        val x = PAGE_WIDTH - PAGE_MARGIN - PdfText.width(value, size, bold)
        text(x, top, value, size, color, bold)
    }

    fun toPage(): PdfCanvasPage = PdfCanvasPage(ops = ops, links = links)

    private companion object {
        const val PAGE_WIDTH = 595f
        const val PAGE_HEIGHT = 842f
    }
}
